import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import AppRouteLoadingFallback from '@/components/routing/AppRouteLoadingFallback';
import { ROLES } from '@/config/roles';
import { useAuth } from '@/hooks/useAuth';
import MaintenancePage from '@/pages/MaintenancePage';
import { getDefaultRedirectPath, hasRouteAccess } from '@/utils/accessControl';
import { landingStateFor } from '@/utils/teacherEventSource';

interface EnhancedProtectedRouteProps {
  children: React.ReactNode;
  /** Explicit policy key for a parameterized route. */
  accessRoute?: string;
}

/**
 * Surfaces locked while the placement diagnostic is owed. Everything a
 * student could practice, review, or be assigned lives under these prefixes;
 * the diagnostic page itself is deliberately NOT here — the gate blocks
 * other practice, never the diagnostic.
 */
const DIAGNOSTIC_GATED_PREFIXES = [
  '/tasks',
  '/practice',
  '/progress',
  '/history',
  '/homework',
] as const;

const isDiagnosticGatedPath = (pathname: string): boolean =>
  DIAGNOSTIC_GATED_PREFIXES.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );

function EnhancedProtectedRoute({
  children,
  accessRoute,
}: EnhancedProtectedRouteProps) {
  const {
    isAuthenticated,
    isAuthLoading,
    user,
    userRole,
    userStatus,
    isRoleLoaded,
    isServerDown,
    loginWithPath,
  } = useAuth();
  const location = useLocation();

  // Show maintenance page if server is down
  if (isServerDown) {
    return <MaintenancePage />;
  }

  // Wait for Auth0 silent re-auth and the role fetch to settle before deciding
  // whether to redirect — otherwise we'd treat a still-loading session as
  // logged-out and bounce the user through Auth0, losing their place.
  if (isAuthLoading || !isRoleLoaded) {
    return <AppRouteLoadingFallback />;
  }

  // Handle authentication
  if (!isAuthenticated) {
    loginWithPath(location.pathname + location.search);
    return <AppRouteLoadingFallback />;
  }

  // If user is inactive, redirect to activation page
  if (userStatus === 'inactive' && location.pathname !== '/pending-approval') {
    return <Navigate to="/pending-approval" replace />;
  }

  // Access gate: a server-computed block on the entitlement access policy
  // (e.g. an expired trial) hard-redirects the student to the block's screen
  // (/pending-approval, which renders "trial ended" copy + a WhatsApp CTA).
  // Keyed off the SERVER decision on the role payload — never client
  // heuristics — so it survives reload and can't be spoofed. Generic on
  // purpose: a new block reason needs no change here.
  if (user?.accessBlock && location.pathname !== user.accessBlock.redirectTo) {
    return <Navigate to={user.accessBlock.redirectTo} replace />;
  }

  // Onboarding gate: a student who still owes the placement diagnostic is
  // hard-redirected off every tasks/practice/progress/history/homework surface
  // until the server stamps completion. Keys off the SERVER-computed `owesDiagnostic`
  // (role payload), never client heuristics.
  // Mirrors the `userStatus === 'inactive'` required-step pattern above.
  if (
    userRole === ROLES.USER &&
    user?.owesDiagnostic === true &&
    isDiagnosticGatedPath(location.pathname)
  ) {
    return <Navigate to="/diagnostic" replace />;
  }

  // Check route-based access using centralized system. Denials go to the
  // role's own default surface — never to `/`, which renders the marketing
  // landing page and reads as "logged out" to a signed-in user.
  if (
    !hasRouteAccess(
      userRole,
      accessRoute ?? location.pathname,
      user ?? undefined
    )
  ) {
    const roleDefault = getDefaultRedirectPath(userRole, user ?? undefined);
    return (
      <Navigate to={roleDefault} replace state={landingStateFor(roleDefault)} />
    );
  }

  return children;
}

export default EnhancedProtectedRoute;
